import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { palettePrimaryMain } from '@/theme/designTokens'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { AppToast } from '@/shared/toast/AppToast'
import {
  APP_TOAST_AUTO_HIDE_MS,
  APP_TOAST_SNACKBAR_ANCHOR,
  APP_TOAST_SNACKBAR_SX,
  shouldDismissAppToastOnSnackbarClose,
} from '@/shared/toast/toastPlacement'
import type { ToastSeverity } from '@/shared/toast/ToastProvider'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SearchIcon from '@mui/icons-material/Search'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import { ApiError } from '@/api/http/types'
import { SensemakerPageTitle } from '@/shared/components/SensemakerPageTitle'
import { StandardPageTitleRow } from '@/shared/components/StandardPageTitleRow'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import {
  sensemakerTableBodyCellSx,
  sensemakerTableBodyCellInteractiveSx,
  sensemakerTableCardSx,
  sensemakerTableHeaderCellSx,
  sensemakerTableSx,
  SENSEMAKER_TABLE,
} from '@/shared/table'
import {
  UserStatusBadge,
  AddUserModal,
  DeactivateMemberModal,
  ReactivateMemberModal,
  ViewMemberModal,
  type UserStatus,
} from '../components'
import { UserListTableSkeleton } from '../components/skeletons'
import {
  useTeamMembers,
  useTeamMemberMutations,
  type TeamMemberRow,
  type TeamListRoleFilter,
  type TeamListStatusFilter,
} from '../api'

const ROLE_FILTER_ALL = 'all'
const STATUS_FILTER_ALL = 'all'

const STATUS_FILTER_TO_DISPLAY: Record<string, UserStatus> = {
  active: 'Active',
  pending: 'Pending',
  expired: 'Expired',
  deactivated: 'Deactivated',
}

/** Defense in depth — table rows must match active role filter. */
function filterRowsByRoleFilter(
  rows: TeamMemberRow[],
  roleFilter: string,
): TeamMemberRow[] {
  if (roleFilter === ROLE_FILTER_ALL) return rows
  const expected = roleFilter.toUpperCase()
  return rows.filter((row) => row._raw.role.toUpperCase() === expected)
}

/** Defense in depth — table rows must match active status filter. */
function filterRowsByStatusFilter(
  rows: TeamMemberRow[],
  statusFilter: string,
): TeamMemberRow[] {
  if (statusFilter === STATUS_FILTER_ALL) return rows
  const expected = STATUS_FILTER_TO_DISPLAY[statusFilter]
  if (!expected) return rows
  return rows.filter((row) => row.status === expected)
}

function mutationErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Something went wrong.'
}

const filterLabelSx = {
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#04183f',
  mb: '8px',
  display: 'block',
} as const

const filterControlSx = {
  height: '42px',
  width: '100%',
  bgcolor: '#ffffff',
  borderRadius: '10px',
  fontSize: '14px',
  letterSpacing: '-0.1504px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e1e7f3' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e1e7f3' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e1e7f3',
    borderWidth: '1px',
  },
} as const

/** Fixed column widths — sort / data changes must not reflow headers. */
const USER_TABLE_COL_WIDTHS = ['8%', '16%', '22%', '10%', '12%', '12%', '20%'] as const

function SelectDropdownIcon(props: object) {
  return <KeyboardArrowDownIcon {...props} sx={{ fontSize: 20, color: '#4a5565', mr: '12px' }} />
}

function EyeActionIcon() {
  return (
    <svg width="22" height="18" viewBox="0 0 21.6373 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.8187 0C16.2108 0 20.6968 3.87976 21.6373 9C20.6968 14.1202 16.2108 18 10.8187 18C5.42648 18 0.94051 14.1202 0 9C0.94051 3.87976 5.42648 0 10.8187 0ZM10.8187 16C15.0543 16 18.6787 13.052 19.5961 9C18.6787 4.94803 15.0543 2 10.8187 2C6.58296 2 2.95858 4.94803 2.04114 9C2.95858 13.052 6.58296 16 10.8187 16ZM10.8187 13.5C8.33334 13.5 6.31862 11.4853 6.31862 9C6.31862 6.51472 8.33334 4.5 10.8187 4.5C13.3039 4.5 15.3187 6.51472 15.3187 9C15.3187 11.4853 13.3039 13.5 10.8187 13.5ZM10.8187 11.5C12.1994 11.5 13.3187 10.3807 13.3187 9C13.3187 7.6193 12.1994 6.5 10.8187 6.5C9.43796 6.5 8.31862 7.6193 8.31862 9C8.31862 10.3807 9.43796 11.5 10.8187 11.5Z" fill="#1976D2" />
    </svg>
  )
}

function UserForbidActionIcon() {
  return (
    <svg width="19" height="22" viewBox="0 0 19 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 6C4 3.79086 5.79086 2 8 2C10.2091 2 12 3.79086 12 6C12 8.20914 10.2091 10 8 10C5.79086 10 4 8.20914 4 6ZM8 0C4.68629 0 2 2.68629 2 6C2 9.3137 4.68629 12 8 12C11.3137 12 14 9.3137 14 6C14 2.68629 11.3137 0 8 0ZM11 17C11 15.3431 12.3431 14 14 14C14.4631 14 14.9018 14.105 15.2934 14.2924L11.2924 18.2934C11.105 17.9018 11 17.4631 11 17ZM12.7066 19.7076L16.7076 15.7066C16.895 16.0982 17 16.5369 17 17C17 18.6569 15.6569 20 14 20C13.5369 20 13.0982 19.895 12.7066 19.7076ZM14 12C11.2386 12 9 14.2386 9 17C9 19.7614 11.2386 22 14 22C16.7614 22 19 19.7614 19 17C19 14.2386 16.7614 12 14 12ZM8 13C8.0843 13 8.1683 13.0013 8.252 13.0039C7.8236 13.6189 7.4914 14.3059 7.2772 15.0431C4.30431 15.4 2 17.9309 2 21H0C0 16.5817 3.58172 13 8 13Z" fill="#FF5959" />
    </svg>
  )
}

function MailSendActionIcon() {
  return (
    <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 0C21.5523 0 22 0.44772 22 1V17.0066C22 17.5552 21.5447 18 21.0082 18H2.9918C2.44405 18 2 17.5551 2 17.0066V16H20V4.3L12 11.5L2 2.5V1C2 0.44772 2.44772 0 3 0H21ZM8 12V14H0V12H8ZM5 7V9H0V7H5ZM19.5659 2H4.43414L12 8.8093L19.5659 2Z" fill="#4CAF51" />
    </svg>
  )
}

function ReactivateActionIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 14.252V16.3414C13.3744 16.1203 12.7013 16 12 16C8.68629 16 6 18.6863 6 22H4C4 17.5817 7.58172 14 12 14C12.6906 14 13.3608 14.0875 14 14.252ZM12 13C8.685 13 6 10.315 6 7C6 3.685 8.685 1 12 1C15.315 1 18 3.685 18 7C18 10.315 15.315 13 12 13ZM12 11C14.21 11 16 9.21 16 7C16 4.79 14.21 3 12 3C9.79 3 8 4.79 8 7C8 9.21 9.79 11 12 11ZM17.7929 19.9142L21.3284 16.3787L22.7426 17.7929L17.7929 22.7426L14.2574 19.2071L15.6716 17.7929L17.7929 19.9142Z" fill="#4CAF51"/>
    </svg>
  )
}

interface RowActionsProps {
  status: UserStatus
  onView: () => void
  onDeactivate: () => void
  onReactivate: () => void
  onResend: () => void
  resendDisabled?: boolean
}

function RowActions({
  status,
  onView,
  onDeactivate,
  onReactivate,
  onResend,
  resendDisabled = false,
}: Readonly<RowActionsProps>) {
  return (
    <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <IconButton size="small" sx={{ p: 0 }} onClick={onView} aria-label="View user">
        <EyeActionIcon />
      </IconButton>
      {status === 'Active' && (
        <IconButton size="small" sx={{ p: 0 }} onClick={onDeactivate} aria-label="Deactivate user">
          <UserForbidActionIcon />
        </IconButton>
      )}
      {status === 'Deactivated' && (
        <IconButton size="small" sx={{ p: 0 }} onClick={onReactivate} aria-label="Reactivate user">
          <ReactivateActionIcon />
        </IconButton>
      )}
      {(status === 'Pending' || status === 'Expired') && (
        <IconButton
          size="small"
          sx={{ p: 0 }}
          onClick={onResend}
          disabled={resendDisabled}
          aria-label="Resend invite"
        >
          <MailSendActionIcon />
        </IconButton>
      )}
    </Box>
  )
}

export function UserListPage() {
  useDocumentTitle('Users')
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [deactivateOpen, setDeactivateOpen] = useState(false)
  const [reactivateOpen, setReactivateOpen] = useState(false)
  const [viewMemberId, setViewMemberId] = useState<string | null>(null)
  const [actionMember, setActionMember] = useState<TeamMemberRow | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: ToastSeverity
  }>({ open: false, message: '', severity: 'success' })

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [roleFilter, setRoleFilter] = useState<string>(ROLE_FILTER_ALL)
  const [statusFilter, setStatusFilter] = useState<string>(STATUS_FILTER_ALL)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const t = globalThis.setTimeout(() => setDebouncedSearch(searchInput), 400)
    return () => globalThis.clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, pageSize])

  const handleRoleFilterChange = (value: string) => {
    queryClient.removeQueries({ queryKey: ['team', 'members'] })
    setRoleFilter(value)
    setPage(1)
  }

  const handleStatusFilterChange = (value: string) => {
    queryClient.removeQueries({ queryKey: ['team', 'members'] })
    setStatusFilter(value)
    setPage(1)
  }

  const listParams = useMemo(
    () => ({
      page,
      pageSize,
      role:
        roleFilter === ROLE_FILTER_ALL
          ? undefined
          : (roleFilter as TeamListRoleFilter),
      status:
        statusFilter === STATUS_FILTER_ALL
          ? undefined
          : (statusFilter as TeamListStatusFilter),
      search: debouncedSearch.trim() || undefined,
      sortBy: 'joinDate' as const,
      sortOrder: 'desc' as const,
    }),
    [page, pageSize, roleFilter, statusFilter, debouncedSearch],
  )

  const { data, isFetching, isPending, isError, error } = useTeamMembers(listParams)
  const { deactivate, reactivate, resendInvitation, exportCsv } = useTeamMemberMutations()

  const isListBusy = isPending || isFetching

  const exportFilters = useMemo(
    () => ({
      role: listParams.role,
      status: listParams.status,
      search: listParams.search,
      sortBy: listParams.sortBy,
      sortOrder: listParams.sortOrder,
    }),
    [listParams.role, listParams.status, listParams.search, listParams.sortBy, listParams.sortOrder],
  )

  const rows = useMemo(() => {
    if (isListBusy) return []
    const raw = data?.rows ?? []
    return filterRowsByStatusFilter(
      filterRowsByRoleFilter(raw, roleFilter),
      statusFilter,
    )
  }, [isListBusy, data?.rows, roleFilter, statusFilter])

  const total = isListBusy ? 0 : (data?.total ?? 0)
  const activePage = isListBusy ? page : (data?.page ?? page)
  const totalPages = Math.max(1, isListBusy ? 1 : (data?.totalPages ?? 1))
  const perPage = data?.perPage ?? pageSize
  const rangeStart = total === 0 ? 0 : (activePage - 1) * perPage + 1
  const rangeEnd = total === 0 ? 0 : Math.min(activePage * perPage, total)

  const hasActiveFilters =
    roleFilter !== ROLE_FILTER_ALL ||
    statusFilter !== STATUS_FILTER_ALL ||
    debouncedSearch.trim().length > 0

  const clearFilters = () => {
    queryClient.removeQueries({ queryKey: ['team', 'members'] })
    setRoleFilter(ROLE_FILTER_ALL)
    setStatusFilter(STATUS_FILTER_ALL)
    setSearchInput('')
    setDebouncedSearch('')
  }

  const showSnackbar = (message: string, severity: ToastSeverity) => {
    setSnackbar({ open: true, message, severity })
  }

  const addMemberCtaSx = {
    mt: 3,
    bgcolor: palettePrimaryMain,
    borderRadius: '10px',
    height: '40px',
    px: '24px',
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: '-0.1504px',
    textTransform: 'none' as const,
    '&:hover': { bgcolor: '#0077b3' },
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <StandardPageTitleRow
        marginBottomPx={0}
        actions={
          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0 }}>
            <Button
              variant="outlined"
              disableElevation
              disabled={exportCsv.isPending || isListBusy || total === 0}
              onClick={() =>
                exportCsv.mutate(exportFilters, {
                  onSuccess: () => showSnackbar('Export downloaded.', 'success'),
                  onError: (err) => showSnackbar(mutationErrorMessage(err), 'error'),
                })
              }
              sx={{
                borderColor: palettePrimaryMain,
                color: palettePrimaryMain,
                bgcolor: 'transparent',
                borderRadius: '10px',
                height: '40px',
                px: '20px',
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '-0.1504px',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: '#0077b3', color: '#0077b3', bgcolor: 'transparent' },
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => setModalOpen(true)}
              sx={{
                bgcolor: palettePrimaryMain,
                borderRadius: '10px',
                height: '40px',
                px: '20px',
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '-0.1504px',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#0077b3' },
              }}
            >
              Add New User
            </Button>
          </Box>
        }
      >
        <SensemakerPageTitle
          title="User Management"
          description="Manage your team members and their campaign access."
        />
      </StandardPageTitleRow>

      {isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error instanceof Error ? error.message : 'Failed to load team members.'}
        </Alert>
      )}

      {/* Filter card */}
      <Box
        sx={{
          bgcolor: '#ffffff',
          border: '1px solid #e1e7f3',
          borderRadius: '10px',
          p: '25px 25px 24px',
          mt: '24px',
        }}
      >
        <Box sx={{ display: 'flex', gap: '24px' }}>
          {/* Search */}
          <Box sx={{ flex: 1 }}>
            <Typography component="label" sx={filterLabelSx}>
              Search
            </Typography>
            <OutlinedInput
              placeholder="Search by ID, name, campaign..."
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              startAdornment={
                <InputAdornment position="start" sx={{ ml: '4px', mr: 0 }}>
                  <SearchIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                </InputAdornment>
              }
              sx={{
                ...filterControlSx,
                '& .MuiOutlinedInput-input': {
                  pl: '8px',
                  pr: '16px',
                  py: '10px',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#9ca3af',
                  letterSpacing: '-0.1504px',
                  lineHeight: 'normal',
                  '&::placeholder': { color: '#9ca3af', opacity: 1 },
                },
              }}
            />
          </Box>

          {/* User Roles */}
          <Box sx={{ flex: 1 }}>
            <Typography component="label" sx={filterLabelSx}>
              User Roles
            </Typography>
            <Select
              value={roleFilter}
              onChange={(e) => handleRoleFilterChange(e.target.value)}
              displayEmpty
              IconComponent={SelectDropdownIcon}
              sx={{
                ...filterControlSx,
                '& .MuiSelect-select': {
                  py: '10px',
                  pl: '20px',
                  pr: '40px !important',
                  fontSize: '14px',
                  color: '#0f172a',
                  letterSpacing: '-0.1504px',
                },
              }}
            >
              <MenuItem value={ROLE_FILTER_ALL}>All</MenuItem>
              <MenuItem value="EDITOR">Editor</MenuItem>
              <MenuItem value="VIEWER">Viewer</MenuItem>
            </Select>
          </Box>

          {/* Status */}
          <Box sx={{ flex: 1 }}>
            <Typography component="label" sx={filterLabelSx}>
              Status
            </Typography>
            <Select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              displayEmpty
              IconComponent={SelectDropdownIcon}
              sx={{
                ...filterControlSx,
                '& .MuiSelect-select': {
                  py: '10px',
                  pl: '20px',
                  pr: '40px !important',
                  fontSize: '14px',
                  color: '#0f172a',
                  letterSpacing: '-0.1504px',
                },
              }}
            >
              <MenuItem value={STATUS_FILTER_ALL}>All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="deactivated">Deactivated</MenuItem>
            </Select>
          </Box>
        </Box>
      </Box>

      {/* Table card */}
      <Box
        sx={{
          ...sensemakerTableCardSx,
          mt: '24px',
          minHeight: rows.length === 0 && !isListBusy ? 320 : 200,
        }}
      >
        <Table size="small" sx={sensemakerTableSx}>
          <colgroup>
            {USER_TABLE_COL_WIDTHS.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <TableHead
            sx={{
              bgcolor: SENSEMAKER_TABLE.headerBg,
              '& .MuiTableCell-head': {
                color: SENSEMAKER_TABLE.headerText,
                bgcolor: SENSEMAKER_TABLE.headerBg,
              },
            }}
          >
            <TableRow>
              <TableCell sx={sensemakerTableHeaderCellSx}>ID</TableCell>
              <TableCell sx={sensemakerTableHeaderCellSx}>Name</TableCell>
              <TableCell sx={sensemakerTableHeaderCellSx}>Email</TableCell>
              <TableCell sx={sensemakerTableHeaderCellSx}>Role</TableCell>
              <TableCell sx={sensemakerTableHeaderCellSx}>Status</TableCell>
              <TableCell sx={sensemakerTableHeaderCellSx}>Join Date</TableCell>
              <TableCell sx={{ ...sensemakerTableHeaderCellSx, textAlign: 'right', overflow: 'visible', maxWidth: 'none' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            aria-busy={isListBusy}
            data-testid={isListBusy ? 'user-list-table-skeleton' : undefined}
          >
            {isListBusy ? <UserListTableSkeleton rowCount={pageSize} /> : null}
            {!isListBusy && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ borderBottom: 0, p: 0, verticalAlign: 'middle' }}>
                  <Box
                    sx={{
                      py: 5,
                      px: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      minHeight: 260,
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 88,
                        height: 88,
                        borderRadius: '50%',
                        bgcolor: '#f0f9ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                      }}
                    >
                      <PeopleOutlineIcon sx={{ fontSize: 44, color: palettePrimaryMain }} aria-hidden />
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '26px',
                        color: '#23262f',
                        letterSpacing: '-0.2px',
                      }}
                    >
                      {hasActiveFilters ? 'No results found' : 'No team members yet'}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 1,
                        maxWidth: 440,
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '22px',
                        color: '#4a5565',
                        letterSpacing: '-0.15px',
                      }}
                    >
                      {hasActiveFilters
                        ? 'Try changing your search or filters, or clear them to see everyone on the team.'
                        : 'Invite editors and viewers by email. They will show up here with status Pending until they complete onboarding.'}
                    </Typography>
                    {hasActiveFilters ? (
                      <Button
                        variant="outlined"
                        onClick={clearFilters}
                        sx={{
                          mt: 3,
                          borderColor: palettePrimaryMain,
                          color: palettePrimaryMain,
                          borderRadius: '10px',
                          height: '40px',
                          px: '20px',
                          fontSize: '14px',
                          fontWeight: 500,
                          textTransform: 'none',
                          '&:hover': { borderColor: '#0077b3', color: '#0077b3', bgcolor: 'rgba(0,140,208,0.04)' },
                        }}
                      >
                        Clear filters
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        disableElevation
                        onClick={() => setModalOpen(true)}
                        sx={addMemberCtaSx}
                      >
                        Add New User
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {rows.map((user) => (
              <TableRow key={user.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell
                  sx={{
                    ...sensemakerTableBodyCellSx,
                    fontWeight: 500,
                    color: SENSEMAKER_TABLE.primaryTextColor,
                  }}
                >
                  {user.id}
                </TableCell>
                <TableCell
                  sx={{
                    ...sensemakerTableBodyCellSx,
                    fontWeight: 400,
                    color: SENSEMAKER_TABLE.primaryTextColor,
                  }}
                >
                  {user.name}
                </TableCell>
                <TableCell
                  sx={{
                    ...sensemakerTableBodyCellSx,
                    fontWeight: 400,
                    color: SENSEMAKER_TABLE.secondaryTextColor,
                  }}
                >
                  {user.email}
                </TableCell>
                <TableCell
                  sx={{
                    ...sensemakerTableBodyCellSx,
                    fontWeight: 400,
                    color: SENSEMAKER_TABLE.primaryTextColor,
                  }}
                >
                  {user.role}
                </TableCell>
                <TableCell sx={sensemakerTableBodyCellInteractiveSx}>
                  <UserStatusBadge status={user.status} />
                </TableCell>
                <TableCell
                  sx={{
                    ...sensemakerTableBodyCellSx,
                    fontWeight: 400,
                    color: SENSEMAKER_TABLE.secondaryTextColor,
                  }}
                >
                  {user.joinDate}
                </TableCell>
                <TableCell sx={sensemakerTableBodyCellInteractiveSx}>
                  <RowActions
                    status={user.status}
                    onView={() => setViewMemberId(user._raw._id)}
                    onDeactivate={() => {
                      setActionMember(user)
                      setDeactivateOpen(true)
                    }}
                    onReactivate={() => {
                      setActionMember(user)
                      setReactivateOpen(true)
                    }}
                    onResend={() => {
                      resendInvitation.mutate(user._raw._id, {
                        onSuccess: () =>
                          showSnackbar('Invitation resent successfully.', 'success'),
                        onError: (err) =>
                          showSnackbar(mutationErrorMessage(err), 'error'),
                      })
                    }}
                    resendDisabled={resendInvitation.isPending}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Pagination — outside the table card, 32px below */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '16px',
          mt: '32px',
          pr: '8px',
        }}
      >
        <Typography sx={{ fontSize: '14px', lineHeight: '21px', color: '#4a5565' }}>
          Rows per page:
        </Typography>
        <Select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          IconComponent={SelectDropdownIcon}
          sx={{
            minWidth: 72,
            height: 36,
            fontSize: '14px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e1e7f3' },
          }}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </Select>
        <Typography sx={{ fontSize: '14px', lineHeight: '21px', color: '#4a5565' }}>
          {total === 0 ? '0 of 0' : `${rangeStart}–${rangeEnd} of ${total}`}
        </Typography>
        <Box sx={{ display: 'flex' }}>
          <IconButton
            size="small"
            disabled={page <= 1 || isListBusy}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            <NavigateBeforeIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={page >= totalPages || isListBusy}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            <NavigateNextIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Box>
      </Box>

      <AddUserModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onInvited={() => showSnackbar('Invitation sent successfully.', 'success')}
        onInviteFailed={(message) => showSnackbar(message, 'error')}
      />
      <DeactivateMemberModal
        open={deactivateOpen}
        onClose={() => {
          if (deactivate.isPending) return
          setDeactivateOpen(false)
          setActionMember(null)
        }}
        memberName={actionMember?.name}
        isPending={deactivate.isPending}
        onConfirm={() => {
          if (!actionMember) return
          deactivate.mutate(actionMember._raw._id, {
            onSuccess: () => {
              setDeactivateOpen(false)
              setActionMember(null)
              showSnackbar('Member deactivated.', 'success')
            },
            onError: (err) => showSnackbar(mutationErrorMessage(err), 'error'),
          })
        }}
      />
      <ReactivateMemberModal
        open={reactivateOpen}
        onClose={() => {
          if (reactivate.isPending) return
          setReactivateOpen(false)
          setActionMember(null)
        }}
        memberName={actionMember?.name}
        isPending={reactivate.isPending}
        onConfirm={() => {
          if (!actionMember) return
          reactivate.mutate(actionMember._raw._id, {
            onSuccess: () => {
              setReactivateOpen(false)
              setActionMember(null)
              showSnackbar('Member reactivated.', 'success')
            },
            onError: (err) => showSnackbar(mutationErrorMessage(err), 'error'),
          })
        }}
      />
      <ViewMemberModal
        open={viewMemberId !== null}
        memberId={viewMemberId}
        onClose={() => setViewMemberId(null)}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={APP_TOAST_AUTO_HIDE_MS}
        onClose={(_event, reason) => {
          if (shouldDismissAppToastOnSnackbarClose(reason)) {
            setSnackbar((s) => ({ ...s, open: false }))
          }
        }}
        anchorOrigin={APP_TOAST_SNACKBAR_ANCHOR}
        sx={APP_TOAST_SNACKBAR_SX}
      >
        <AppToast
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        />
      </Snackbar>
    </Box>
  )
}
